import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/#features", label: "Features" },
  { to: "/founder", label: "About" },
  { to: "/contact", label: "Support" },
];

export const FOOTER_LINKS = [
  { to: "/", label: "Home" },
  { to: "/#features", label: "Features" },
  { to: "/contact", label: "Contact / Support" },
  { to: "/founder", label: "About Founder" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/delete-account", label: "Delete Account" },
];

function Header() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[var(--pm-border)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[76px] flex items-center justify-between">
        <Wordmark />
        <nav className="hidden md:flex items-center gap-8" aria-label="Main">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} className="text-sm font-semibold text-[#3B3858] hover:text-[var(--pm-primary)] transition-colors" data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</Link>
          ))}
          {user ? (
            <Link to="/dashboard" className="pm-btn pm-btn-primary" data-testid="nav-open-dashboard">Open Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-[#3B3858] hover:text-[var(--pm-primary)]" data-testid="nav-signin">Sign in</Link>
              <Link to="/login" className="pm-btn pm-btn-primary" data-testid="nav-get-started">Get Started</Link>
            </>
          )}
        </nav>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="public-mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[var(--pm-border)] bg-white px-5 py-4 flex flex-col gap-3" data-testid="public-mobile-menu">
          {NAV.map((n) => <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="font-semibold py-1">{n.label}</Link>)}
          <Link to={user ? "/dashboard" : "/login"} className="pm-btn pm-btn-primary" onClick={() => setOpen(false)}>{user ? "Open Dashboard" : "Sign in / Get Started"}</Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--pm-border)] bg-white mt-24" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
        <p className="text-sm text-[var(--pm-muted)]">
          © {new Date().getFullYear()} {BRAND.name} · {BRAND.tagline} ·{" "}
          <a href={`mailto:${BRAND.email}`} className="hover:text-[var(--pm-primary)]" data-testid="footer-email">{BRAND.email}</a>
        </p>
        <nav className="flex flex-wrap gap-x-6 gap-y-2" aria-label="Footer">
          {FOOTER_LINKS.map((l) => (
            <NavLink key={l.label} to={l.to} className="text-sm text-[var(--pm-muted)] hover:text-[var(--pm-primary)]" data-testid={`footer-link-${l.to.replace(/[/#]/g, "") || "home"}`}>{l.label}</NavLink>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--pm-bg)]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export const LegalShell = ({ eyebrow, title, children, testid }) => (
  <PublicLayout>
    <article className="max-w-3xl mx-auto px-5 lg:px-0 pt-14 pm-fade-up" data-testid={testid}>
      <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#A0136E] mb-3">{eyebrow}</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold mb-8">{title}</h1>
      <div className="pm-card p-6 sm:p-10 pm-prose">{children}</div>
    </article>
  </PublicLayout>
);
